/**
 * DealCardContent - Reusable deal display content.
 *
 * Shows deal name, amount/close date subtitle, and optional nested
 * entity cards via the `children` slot. Callers render associated
 * entities (contacts, companies, etc.) as children — DealCardContent
 * does not own the association rendering logic.
 *
 * When no children are provided and a `company` prop is given, renders
 * a default nested company card for backward compatibility.
 *
 * Props-based: accepts pre-extracted string values so it works in both
 * CRM contexts (CrmObject) and proposal action contexts (flat payload).
 */

import type { ReactNode } from 'react';
import { useCallback, useSyncExternalStore } from 'react';

import { useOptionalOrg } from '@/hooks/useOptionalOrg';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { useCrmObjectSnapshot } from '@/lib/crm/hooks';
import {
  getFailureVersion,
  hasFailedFetch,
  subscribeToFetchFailures,
} from '@/lib/crm/object-cards/entityFetchBatcher';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import { CompactCardLayout } from './CompactCardLayout';
import type { CompanyCardContentProps } from './CompanyCardContent';
import { CompanyCardContent } from './CompanyCardContent';
import { formatCloseDateString } from './formatCloseDate';
import { ObjectCard } from './ObjectCard';

export interface DealCardContentProps {
  /**
   * Store-fetch mode: when `entityId` is provided, the card subscribes to
   * the CRM store and fills in any undefined fields from the live object.
   * The nested company sub-card is derived from the deal's
   * `ahoy_deal_to_company` association when the deal is in store-fetch
   * mode — CompanyCardContent handles its own per-field loading so
   * nothing pops in from an empty placeholder.
   */
  entityId?: string;
  orgId?: number;

  /** Explicit override — wins over store-resolved value. */
  name?: string;
  /** Formatted total amount (e.g., "$1,500") */
  totalAmount?: string;
  /** Formatted close date (e.g., "April 14th") */
  closeDate?: string;
  /**
   * Nested company card data — rendered as a bordered ObjectCard when
   * no children are provided. Ignored when children are present (callers
   * control nested cards via children instead).
   */
  company?: CompanyCardContentProps;
  /**
   * How the company renders in the full layout. `card` (default) nests a
   * bordered CompanyCardContent below the subtitle. `chip` folds the company
   * into the subtitle line as an inline badge — for surfaces where the deal
   * is context rather than the hero.
   */
  nestedCompany?: 'card' | 'chip';
  /** When provided, clicking the name triggers this callback (e.g. open CRM slideout) */
  onClick?: () => void;
  /** Compact mode — avatar + name + type pill only */
  compact?: boolean;
  /** Compact mode only — omit the type pill (see CompactCardLayout). */
  hideTypeBadge?: boolean;
  /**
   * Nested entity cards slot. When provided, replaces the default
   * company card rendering. Use this to render a list of associated
   * entities (contacts, companies, etc.) as bordered ObjectCards.
   */
  children?: ReactNode;
  className?: string;
}

const EMPTY = '\u2013';

/**
 * The `nestedCompany='chip'` subtitle: company badge · amount · close date,
 * rendering only the parts that are known. Separated by a middle dot so it
 * reads as one line of context under the deal name.
 */
function DealChipRow({
  company,
  orgId,
  totalAmount,
  closeDate,
  leadingSeparator = false,
}: {
  company: CompanyCardContentProps | undefined;
  orgId: number | undefined;
  totalAmount: string | undefined;
  closeDate: string | undefined;
  /** Prefix the first part with a separator too (it follows the name inline). */
  leadingSeparator?: boolean;
}) {
  // An explicit orgId wins; otherwise the org context supplies it (action
  // cards render inside TeamProvider but don't always thread orgId down).
  const contextOrgId = useOptionalOrg()?.currentOrg?.id;
  const badgeOrgId = orgId ?? contextOrgId;
  const parts: { key: string; node: ReactNode }[] = [];
  if (company?.entityId && badgeOrgId !== undefined) {
    parts.push({
      key: 'company',
      node: (
        <EntityBadgeById
          entityId={company.entityId}
          orgId={badgeOrgId}
          variant='xs'
        />
      ),
    });
  } else if (company?.name) {
    parts.push({
      key: 'company',
      node: (
        <span className='font-medium text-fg-secondary'>{company.name}</span>
      ),
    });
  }
  if (totalAmount) {
    parts.push({
      key: 'amount',
      node: <span className='tabular-nums'>{totalAmount}</span>,
    });
  }
  if (closeDate) {
    parts.push({ key: 'close', node: <span>Closes {closeDate}</span> });
  }
  if (parts.length === 0) {
    return null;
  }
  return (
    <span className='inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-tertiary'>
      {parts.map((part, index) => (
        <span key={part.key} className='inline-flex items-center gap-2'>
          {(index > 0 || leadingSeparator) && (
            <span className='text-fg-quaternary' aria-hidden='true'>
              ·
            </span>
          )}
          {part.node}
        </span>
      ))}
    </span>
  );
}

/**
 * Build the "Total: $X \u2022 Close Date Apr 14" subtitle. Returns null when
 * BOTH fields are empty so the caller can hide the row entirely rather
 * than paint "Total: \u2013 \u2022 Close Date \u2013" \u2014 a subtitle with nothing but
 * placeholders reads as broken. When one is known and the other missing,
 * the missing side still renders as an en-dash so the pair stays legible.
 */
function buildSubtitle(
  totalAmount?: string,
  closeDate?: string
): string | null {
  if (!totalAmount && !closeDate) {
    return null;
  }
  const amount = totalAmount ? `Total: ${totalAmount}` : `Total: ${EMPTY}`;
  const close = closeDate ? `Close Date ${closeDate}` : `Close Date ${EMPTY}`;
  return `${amount} \u2022 ${close}`;
}

function readStringProp(
  object: CrmObject | undefined,
  key: string
): string | undefined {
  if (!object?.properties) {
    return undefined;
  }
  const raw = getPropertyValue<string | string[]>(object.properties[key]);
  if (typeof raw === 'string') {
    return raw;
  }
  if (Array.isArray(raw) && typeof raw[0] === 'string') {
    return raw[0];
  }
  return undefined;
}

function formatCurrencyFromProperty(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'object' && 'amount' in (value as object)) {
    const obj = value as {
      amount: string | number | null | undefined;
      unit?: string;
    };
    if (obj.amount === null || obj.amount === undefined) {
      return undefined;
    }
    const num =
      typeof obj.amount === 'string' ? parseFloat(obj.amount) : obj.amount;
    if (typeof num !== 'number' || Number.isNaN(num)) {
      return undefined;
    }
    if (obj.unit) {
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: obj.unit,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(num);
      } catch {
        return `${num.toLocaleString()} ${obj.unit}`;
      }
    }
    return num.toLocaleString();
  }
  return undefined;
}

export function DealCardContent(props: DealCardContentProps) {
  const {
    entityId,
    orgId,
    name: nameProp,
    totalAmount: totalAmountProp,
    closeDate: closeDateProp,
    company: companyProp,
    nestedCompany = 'card',
    onClick,
    compact,
    hideTypeBadge,
    children,
    className,
  } = props;

  // Subscribe to the deal's store entry \u2014 resolves name / amount / close
  // date / nested company association.
  const storeObject = useCrmObjectSnapshot(entityId, orgId);
  // Subscribe to the failure registry so React re-renders when a fetch fails.
  useSyncExternalStore(
    subscribeToFetchFailures,
    getFailureVersion,
    getFailureVersion
  );
  const entityFetchFailed = entityId != null && hasFailedFetch(entityId);
  const isSourceHydrating =
    entityId != null && !storeObject && !entityFetchFailed;

  const name =
    nameProp ??
    readStringProp(storeObject, 'ahoy_deal_name') ??
    readStringProp(storeObject, 'ahoy_name');
  const isNameLoading = nameProp == null && isSourceHydrating;

  const totalAmount =
    totalAmountProp ??
    formatCurrencyFromProperty(
      storeObject?.properties?.ahoy_amount
        ? getPropertyValue<unknown>(storeObject.properties.ahoy_amount)
        : undefined
    );
  const closeDate =
    closeDateProp ??
    formatCloseDateString(readStringProp(storeObject, 'ahoy_close_date'));
  // Only show the subtitle skeleton while BOTH fields are still unresolved
  // (and the store is hydrating). If one field is already known, render it
  // immediately alongside the existing "–" placeholder for the missing one
  // rather than hiding the known value behind a full-width skeleton.
  const isSubtitleLoading =
    totalAmount == null && closeDate == null && isSourceHydrating;

  // Nested company sub-card. Caller-supplied `company` wins. Otherwise
  // resolve it from the deal's `ahoy_deal_to_company` association \u2014 pass
  // the target entity_id + orgId to CompanyCardContent so it
  // self-hydrates industry / headcount / location without any
  // per-field plumbing at this layer.
  const companyAssocId =
    companyProp == null
      ? storeObject?.associations?.find(
          a => a.association_type_name === 'ahoy_deal_to_company'
        )?.target_entity_id
      : undefined;
  const company: CompanyCardContentProps | undefined =
    companyProp ??
    (companyAssocId ? { entityId: companyAssocId, orgId } : undefined);
  const handleNameClick = useCallback(
    (e: React.MouseEvent) => {
      if (onClick) {
        e.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  const subtitle = buildSubtitle(totalAmount, closeDate);

  // Auto-collapse to compact when nothing under the name has any content —
  // no subtitle values, no nested company, no children. Full mode would
  // otherwise render an empty column for a deal we only have a name for.
  // Only kicks in after hydration settles, and only when the caller hasn't
  // requested an inline chip treatment (chip mode is deliberately inline
  // and doesn't want a card layout swapped under it). Explicit `compact`
  // still wins.
  const hasNestedContent = !!children || !!company;
  const shouldRenderCompact =
    compact ||
    (nestedCompany !== 'chip' &&
      !isSourceHydrating &&
      !isSubtitleLoading &&
      subtitle == null &&
      !hasNestedContent);

  if (shouldRenderCompact) {
    return (
      <CompactCardLayout
        name={name ?? ''}
        entityTypeName='ahoy_deal'
        isLoading={isNameLoading}
        hideTypeBadge={hideTypeBadge}
        onClick={onClick}
        className={className}
      />
    );
  }

  // Chip layout: one subtitle line — company badge · amount · close date.
  // The company badge self-hydrates from its entity id; a name-only company
  // (not yet in the CRM) renders as plain text.
  if (nestedCompany === 'chip') {
    return (
      <div
        className={cx(
          'flex flex-wrap items-center gap-x-2 gap-y-1 text-sm',
          className
        )}
      >
        {isNameLoading ? (
          <Skeleton variant='text' width={180} height={16} />
        ) : (
          <span
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick ? handleNameClick : undefined}
            onKeyDown={onClick ? handleNameKeyDown : undefined}
            className={cx(
              'font-medium text-fg-primary truncate',
              onClick &&
                'cursor-pointer hover:underline hover:text-fg-brand-primary'
            )}
          >
            {name ?? 'New deal'}
          </span>
        )}
        {isSubtitleLoading ? (
          <Skeleton variant='text' width={240} height={12} />
        ) : (
          <DealChipRow
            company={company}
            orgId={orgId}
            totalAmount={totalAmount}
            closeDate={closeDate}
            leadingSeparator
          />
        )}
      </div>
    );
  }

  return (
    <div className={cx('space-y-3', className)}>
      {/* Top row: deal name + subtitle (left-aligned). Skeleton when the
          store is still hydrating the deal and the caller didn't override. */}
      <div>
        {isNameLoading ? (
          <Skeleton variant='text' width={180} height={16} />
        ) : (
          <p
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick ? handleNameClick : undefined}
            onKeyDown={onClick ? handleNameKeyDown : undefined}
            className={cx(
              'text-sm font-semibold text-fg-primary',
              onClick &&
                'cursor-pointer hover:underline hover:text-fg-brand-primary'
            )}
          >
            {name ?? 'New deal'}
          </p>
        )}
        {isSubtitleLoading && (
          <div className='mt-0.5'>
            <Skeleton variant='text' width={240} height={12} />
          </div>
        )}
        {!isSubtitleLoading && subtitle && (
          <p className='text-xs text-fg-secondary mt-0.5'>{subtitle}</p>
        )}
      </div>

      {/* Nested entity cards: children take priority over default company card */}
      {children ??
        (company ? (
          <ObjectCard bordered>
            <CompanyCardContent {...company} />
          </ObjectCard>
        ) : null)}
    </div>
  );
}
